import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Calendar, MapPin, ArrowRight, Loader2, UserCheck } from 'lucide-react';
import { useReferral } from '@/hooks/useReferral';
import { BACKEND_URL } from '@/config/api';

const API = BACKEND_URL;

/*
  /eventos — Listado público de próximos eventos.

  - Llama a GET /api/events (solo eventos publicados, futuros)
  - Cada card enlaza a /eventos/:slug?ref=CODE para preservar el tracking de afiliado
  - Estado vacío amable cuando no hay eventos abiertos
*/

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(d);
  } catch { return iso; }
};

// Local-vs-CDN flyer resolver — bootcamps fall back to a public asset (no DB binary);
// single events serve their image from the backend (/api/events/.../flyer).
const resolveFlyer = (event) => {
  if (!event.flyer_url) return null;
  // Backend-served binaries start with /api/ — prepend BACKEND_URL.
  if (event.flyer_url.startsWith('/api/')) return `${API}${event.flyer_url}`;
  // Static asset bundled with the frontend (e.g. fallback for bootcamps) — leave as-is.
  return event.flyer_url;
};

function EventCard({ event, refCode }) {
  // detail_path is set by the backend per kind (events vs bootcamps).
  // Affiliate code is preserved when navigating to the detail page.
  const basePath = event.detail_path || `/eventos/${event.slug}`;
  const detailUrl = `${basePath}${refCode ? `?ref=${refCode}` : ''}`;
  const isBootcamp = event.kind === 'bootcamp';
  const flyer = resolveFlyer(event);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-[#D4AF37]/40 transition-all relative"
    >
      {/* Type badge — top-right corner */}
      <div className="absolute top-3 right-3 z-10">
        <span
          className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${
            isBootcamp
              ? 'bg-[#0F172A] text-[#D4AF37] border border-[#D4AF37]/40'
              : 'bg-[#D4AF37] text-[#0F172A]'
          }`}
        >
          {isBootcamp ? 'Bootcamp' : 'Evento'}
        </span>
      </div>

      <Link to={detailUrl} className="block">
        <div className="aspect-[3/4] bg-slate-100 overflow-hidden">
          {flyer ? (
            <img
              src={flyer}
              alt={event.title}
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
              Sin imagen
            </div>
          )}
        </div>
      </Link>

      <div className="p-5">
        <Link to={detailUrl} className="block">
          <h3 className="font-heading text-lg text-[#0F172A] font-semibold leading-tight mb-1 hover:text-[#D4AF37] transition-colors">
            {event.title}
          </h3>
          {isBootcamp && event.number && (
            <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">
              Edición #{event.number}
            </p>
          )}
        </Link>

        <div className="flex items-center gap-2 text-sm text-slate-600 mb-1.5">
          <Calendar className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
          <span className="capitalize">{formatDate(event.event_date)}</span>
        </div>

        {(event.location_name || event.city) && (
          <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
            <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
            <span>{[event.location_name, event.city].filter(Boolean).join(' · ')}</span>
          </div>
        )}

        <Link
          to={detailUrl}
          className="inline-flex items-center gap-1.5 min-h-[40px] text-sm font-semibold text-[#0F172A] hover:text-[#D4AF37] transition-colors"
        >
          {isBootcamp ? 'Ver bootcamp' : 'Reservar plaza'} <ArrowRight size={14} />
        </Link>
      </div>
    </motion.div>
  );
}

export default function EventosListing() {
  const { referralCode, affiliateName } = useReferral();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/events`)
      .then((res) => {
        if (cancelled) return;
        setEvents(res.data?.events || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'No se pudieron cargar los eventos');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white pt-20">

      {/* Hero */}
      <section className="py-12 sm:py-16 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {affiliateName && (
            <p className="text-xs text-[#D4AF37]/80 mb-3 uppercase tracking-wider inline-flex items-center gap-1.5 justify-center">
              <UserCheck size={12} /> Invitación de {affiliateName}
            </p>
          )}
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
            Próximos <span className="text-[#D4AF37] italic">eventos</span>
          </h1>
          <p className="text-base text-white/70 max-w-2xl mx-auto">
            Encuentros presenciales del club en la Costa Blanca: formación en persona, casos reales
            del sector inmobiliario y una comunidad con la que compartir criterio.
          </p>
        </div>
      </section>

      {/* Listing */}
      <section className="py-14 sm:py-20 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-[#D4AF37]" size={28} />
            </div>
          ) : error ? (
            <p className="text-center text-red-500">{error}</p>
          ) : events.length === 0 ? (
            <div className="text-center py-20 max-w-md mx-auto">
              <p className="text-slate-600 mb-2">No hay eventos abiertos en este momento.</p>
              <p className="text-sm text-slate-400 mb-5">
                Escríbenos y te avisaremos cuando abramos el próximo.
              </p>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] border border-[#0F172A]/20 px-5 h-11 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                Contactar con el equipo <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event) => (
                <EventCard key={event.slug} event={event} refCode={referralCode} />
              ))}
            </div>
          )}
        </div>
      </section>

    </div>
  );
}
